import { isErrorWithData } from '@/services/helpers';
import {
  useDeleteOrganizationWebhookMutation,
  useDeleteOrganizationWebhooksSubscriptionMutation,
  useGetOrganizationWebhookLogsQuery,
  useGetOrganizationWebhooksQuery,
  useGetOrganizationWebhooksSubscriptionsQuery,
} from '@/services/organizations';
import { Alert, Box, Button, Icon, Modal, Table, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Key, useCallback, useState } from 'react';

import { WebhookClient, WebhookStatus, WebhookSubscription, WithObjectIdsAsStrings } from 'types';

import { EditWebhookRegistrationModal } from './EditWebhookRegistrationModal';
import { EditWebhookSubscriptionModal } from './EditWebhookSubscriptionModal';

export interface LogColumnProps {
  row: WithObjectIdsAsStrings<WebhookSubscription>;
}
const LogColumn = ({ row }: LogColumnProps) => {
  const { data, isLoading } = useGetOrganizationWebhookLogsQuery(
    { registrationId: row.clientId, subscriptionId: row.id, limit: 10 },
    { skip: !row.id },
  );

  const failed = data?.filter((log: any) => log.status === WebhookStatus.failed).length;

  return (
    <Box childGap="sm" fontSize="xs">
      {isLoading ? '...' : failed}
    </Box>
  );
};

const ExpandedRowRender = (registration: WithObjectIdsAsStrings<WebhookClient>) => {
  const [subscription, setSubscription] = useState<any>(null);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<any>(null);

  const { data, isFetching } = useGetOrganizationWebhooksSubscriptionsQuery(
    { registrationId: registration.id },
    { skip: !registration.id },
  );

  const [deleteSubscription, { isLoading: isDeletingSubscription }] =
    useDeleteOrganizationWebhooksSubscriptionMutation();

  const {
    isOpen: isEditWebhookSubscriptionModalOpen,
    handleClose: handleEditWebhookSubscriptionModalClose,
    handleOpen: handleOpenEditWebhookSubscriptionModal,
  } = useOpenClose();

  const {
    isOpen: isDeleteSubscriptionModalOpen,
    handleClose: handleDeleteSubscriptionModalClose,
    handleOpen: handleOpenDeleteSubscriptionModal,
  } = useOpenClose();

  const handleEditClick = useCallback(
    (subscription: any) => {
      setSubscription(subscription);
      handleOpenEditWebhookSubscriptionModal();
    },
    [handleOpenEditWebhookSubscriptionModal],
  );

  const handleDeleteClick = useCallback(
    (subscription: any) => {
      setSubscriptionToDelete(subscription);
      handleOpenDeleteSubscriptionModal();
    },
    [handleOpenDeleteSubscriptionModal],
  );

  const handleConfirmDeleteSubscription = useCallback(async () => {
    if (!subscriptionToDelete) return;
    try {
      await deleteSubscription({
        registrationId: registration.id,
        subscriptionId: subscriptionToDelete.id,
      }).unwrap();
      toast.success('Subscription deleted');
      handleDeleteSubscriptionModalClose();
      setSubscriptionToDelete(null);
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(e.data.message);
      } else {
        toast.error('Error deleting subscription');
      }
    }
  }, [subscriptionToDelete, deleteSubscription, registration.id, handleDeleteSubscriptionModalClose]);

  const subscriptionColumnConfig = [
    {
      heading: 'Event',
      dataKey: 'eventType',
      render: (_: any, subscription: any) => {
        return (
          <Box childGap="2xs" padding="0" color="body-secondary">
            <code>{subscription.eventType}</code>
          </Box>
        );
      },
    },
    {
      heading: 'HTTP Method',
      render: (_: any, subscription: any) => <code>{subscription.httpMethod}</code>,
    },
    {
      heading: 'Endpoint',
      render: (_: any, subscription: any) => <code>{subscription.endpointUrl}</code>,
    },
    {
      heading: 'Enabled',
      render: (_: any, subscription: any) => (
        <Box childGap="sm" fontSize="xs">
          <Box direction="row" alignItems="center" gap="xs">
            {subscription.isEnabled ? 'YES' : 'NO'}
            {!subscription.isEnabled && <Icon name="c-warning" size="sm" color="warning-500" />}
          </Box>
        </Box>
      ),
    },
    {
      heading: 'Failures',
      render: (_: any, subscription: any) => <LogColumn row={subscription} />,
    },
    {
      heading: 'Actions',
      render: (_: any, subscription: any) => (
        <Box direction="row">
          <Button variant="secondary" onClick={() => handleEditClick(subscription)}>
            <Icon color="body-primary" name="settings" size="sm" />
          </Button>
          <Button variant="tertiary" tone="danger" onClick={() => handleDeleteClick(subscription)}>
            <Icon color="danger" name="trash" size="sm" />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <>
      <EditWebhookSubscriptionModal
        isOpen={isEditWebhookSubscriptionModalOpen}
        handleClose={handleEditWebhookSubscriptionModalClose}
        registration={registration}
        subscription={subscription}
      />
      <Modal
        isOpen={isDeleteSubscriptionModalOpen}
        ariaLabelledBy="deleteSubscriptionModal"
        onDismiss={handleDeleteSubscriptionModalClose}
      >
        <Modal.Header
          id="deleteSubscriptionModal"
          title="Delete Subscription"
          onDismiss={handleDeleteSubscriptionModalClose}
        />
        <Modal.Body background="secondary">
          <Box>
            Are you sure you want to delete this subscription for <strong>{subscriptionToDelete?.eventType}</strong>?
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteSubscriptionModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            tone="danger"
            onClick={handleConfirmDeleteSubscription}
            isLoading={isDeletingSubscription}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      <Table
        isCompact
        isLoading={isFetching}
        rowKey="id"
        columns={subscriptionColumnConfig}
        rows={data ?? []}
        isScrollable={{
          x: true,
          y: false,
        }}
      />
    </>
  );
};

export const WebhookClientTable = ({ organizationAlias }: { organizationAlias: string }) => {
  const [expandedRow, setExpandedRow] = useState<Key | undefined>(undefined);
  const [registration, setRegistration] = useState<WithObjectIdsAsStrings<WebhookClient> | null>(null);
  const [registrationToDelete, setRegistrationToDelete] = useState<WithObjectIdsAsStrings<WebhookClient> | null>(
    null,
  );
  const [registrationForNewSubscription, setRegistrationForNewSubscription] =
    useState<WithObjectIdsAsStrings<WebhookClient> | null>(null);

  const {
    isOpen: isWebhookRegistrationModalOpen,
    handleClose: handleWebhookRegistrationModalClose,
    handleOpen: handleOpenWebhookRegistrationModal,
  } = useOpenClose();

  const {
    isOpen: isDeleteRegistrationModalOpen,
    handleClose: handleDeleteRegistrationModalClose,
    handleOpen: handleOpenDeleteRegistrationModal,
  } = useOpenClose();

  const {
    isOpen: isAddSubscriptionModalOpen,
    handleClose: handleAddSubscriptionModalClose,
    handleOpen: handleOpenAddSubscriptionModal,
  } = useOpenClose();

  const { data, isFetching } = useGetOrganizationWebhooksQuery({ alias: organizationAlias });
  const [deleteWebhook, { isLoading: isDeletingWebhook }] = useDeleteOrganizationWebhookMutation();

  const handleEditClick = useCallback(
    (registration: WithObjectIdsAsStrings<WebhookClient>) => {
      setRegistration(registration);
      handleOpenWebhookRegistrationModal();
    },
    [handleOpenWebhookRegistrationModal],
  );

  const handleAddSubscriptionClick = useCallback(
    (registration: WithObjectIdsAsStrings<WebhookClient>) => {
      setRegistrationForNewSubscription(registration);
      handleOpenAddSubscriptionModal();
    },
    [handleOpenAddSubscriptionModal],
  );

  const handleDeleteClick = useCallback(
    (registration: WithObjectIdsAsStrings<WebhookClient>) => {
      setRegistrationToDelete(registration);
      handleOpenDeleteRegistrationModal();
    },
    [handleOpenDeleteRegistrationModal],
  );

  const handleConfirmDeleteRegistration = useCallback(async () => {
    if (!registrationToDelete) return;
    try {
      await deleteWebhook({
        registrationId: registrationToDelete.id,
      }).unwrap();
      toast.success('Webhook deleted');
      handleDeleteRegistrationModalClose();
      setRegistrationToDelete(null);
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(e.data.message);
      } else {
        toast.error('Error deleting webhook');
      }
    }
  }, [registrationToDelete, deleteWebhook, handleDeleteRegistrationModalClose]);

  const columnConfig = [
    {
      heading: 'Name',
      dataKey: 'name',
      width: 250,
    },
    {
      heading: 'Base URL',
      render: (_: any, row: any) => {
        return (
          <Box childGap="2xs" fontSize="xs" padding="sm 0" color="body-secondary">
            <code>{row.hostUrl}</code>
          </Box>
        );
      },
    },
    {
      heading: 'Sub-orgs', //TODO need a concise name for this. "Bubble Up"?
      width: 100,
      align: 'right' as const,
      render: (_: any, row: any) => (
        <Box childGap="sm" fontSize="xs" alignItems="center">
          <Box>{row.includeVisibleOrgEvents ? 'YES' : 'NO'}</Box>
        </Box>
      ),
    },
    {
      heading: 'Actions',
      width: 180,
      render: (_: any, row: any) => {
        return (
          <Box direction="row" justifyContent="flex-end" alignItems="center">
            <Button variant="secondary" onClick={() => handleEditClick(row)}>
              <Icon color="body-primary" name="settings" size="sm" />
            </Button>
            <Button variant="secondary" onClick={() => handleAddSubscriptionClick(row)}>
              <Icon color="body-primary" name="add" size="sm" />
            </Button>
            <Button variant="tertiary" tone="danger" onClick={() => handleDeleteClick(row)}>
              <Icon color="danger" name="trash" size="sm" />
            </Button>
          </Box>
        );
      },
    },
  ];

  const hasNoWebhooks = !isFetching && data?.length === 0;

  return (
    <>
      {hasNoWebhooks && (
        <Alert
          variant="info"
          title="No webhook registrations configured"
          message="Create a webhook registration using the button above to start receiving event notifications."
        />
      )}
      {data && data.length > 0 && (
        <Box childGap="md" margin="0 0 xl 0">
          <Table
            isLoading={isFetching}
            expandedRow={expandedRow}
            onExpandedRowChange={setExpandedRow}
            expandedRowRender={(row: any) => ExpandedRowRender(row)}
            expandBoxProps={
              {
                cursor: 'pointer',
                fontSize: 'xs',
              } as Partial<React.ComponentProps<typeof Box>>
            }
            expandLabels={{ expand: 'Expand ▼', collapse: 'Collapse ▲' }}
            rowKey="id"
            columns={columnConfig}
            rows={data}
            isScrollable={{
              x: true,
              y: false,
            }}
          />
        </Box>
      )}
      <EditWebhookRegistrationModal
        organizationAlias={organizationAlias}
        registration={registration}
        isOpen={isWebhookRegistrationModalOpen}
        handleClose={handleWebhookRegistrationModalClose}
      />
      <EditWebhookSubscriptionModal
        isOpen={isAddSubscriptionModalOpen}
        handleClose={handleAddSubscriptionModalClose}
        registration={registrationForNewSubscription}
        subscription={null}
      />
      <Modal
        isOpen={isDeleteRegistrationModalOpen}
        ariaLabelledBy="deleteRegistrationModal"
        onDismiss={handleDeleteRegistrationModalClose}
      >
        <Modal.Header
          id="deleteRegistrationModal"
          title="Delete Webhook"
          onDismiss={handleDeleteRegistrationModalClose}
        />
        <Modal.Body background="secondary">
          <Box childGap="sm">
            <Box>
              Are you sure you want to delete the webhook <strong>{registrationToDelete?.name}</strong>?
            </Box>
            <Box fontSize="sm" color="body-secondary">
              This will also delete all subscriptions associated with this webhook.
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteRegistrationModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            tone="danger"
            onClick={handleConfirmDeleteRegistration}
            isLoading={isDeletingWebhook}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
